import { SamplePollLanguage } from '../poll/header.class';

/**
 * In-Memory-Cache fuer die lokalisierten SPA-Shells (`_spa.html`), die die
 * ogProxy Cloud Function zur Laufzeit von Firebase Hosting fetcht.
 *
 * Warum nicht "einmal beim Cold-Start und fertig": Die Shell referenziert
 * gehashte JS-Bundles; nach einem Hosting-Deploy existieren die alten Hashes
 * nicht mehr (der Catch-all liefert dann HTML statt JS, die App bleibt beim
 * Laden haengen). Daher nur eine kurze Mindestfrist (Default 10 s) gegen
 * Request-Stuerme, danach revalidiert die naechste Anfrage. Das Deploy-Fenster
 * ist damit hoechstens `ttlMs` lang statt frueher 5 Minuten (Workorder
 * vote-link-deploy-window-and-chunk-reload 2026-10-30).
 *
 * `If-None-Match` wird mitgeschickt, Hosting antwortet fuer `_spa.html` aber
 * nie mit 304 (Gemessen 2026-10-30: `Cache-Control: no-cache` -> immer
 * CDN-MISS, Origin liefert 200) — jede Revalidierung ist also ein voller,
 * ~40 KB kleiner Fetch. 304 bleibt unterstuetzt, falls Hosting das aendert.
 *
 * stale-if-error: Ist Hosting nicht erreichbar, wird die letzte Shell
 * weiter ausgeliefert und erst nach `errorBackoffMs` neu versucht.
 *
 * `fetchImpl` und `now` sind injizierbar -> voll unit-testbar; der Aufrufer
 * kuemmert sich um Timeouts (z.B. AbortSignal im fetch-Wrapper).
 */

export interface MinimalFetchResponse {
    status: number;
    ok: boolean;
    text(): Promise<string>;
    headers: { get(name: string): string | null };
}

export type MinimalFetch = (
    url: string,
    headers?: Record<string, string>
) => Promise<MinimalFetchResponse>;

export interface ShellCacheOptions {
    fetchImpl: MinimalFetch;
    /** z.B. https://www.whocan.dev — ohne trailing Slash (wird normalisiert). */
    baseUrl: string;
    /** Mindestfrist zwischen zwei Revalidierungen einer Sprache, Default 10 s. */
    ttlMs?: number;
    /** Wartezeit nach einer fehlgeschlagenen Revalidierung (stale-if-error), Default 60 s. */
    errorBackoffMs?: number;
    /** Injizierbar fuer Tests. */
    now?: () => number;
}

export interface GetShellOptions {
    /**
     * Mindestfrist ignorieren und jetzt revalidieren (stale-if-error-Backoff gilt weiter) —
     * fuer Antworten, die das Hosting-CDN cacht: eine dort eingefrorene alte Shell wuerde der
     * CDN-Purge des naechsten Deploys nicht mehr erwischen.
     */
    forceRevalidate?: boolean;
}

export interface ShellCache {
    /** Frische oder revalidierte Shell; wirft nur, wenn gar keine Shell beschaffbar ist. */
    getShell(lang: SamplePollLanguage | null, options?: GetShellOptions): Promise<string>;
    /**
     * Letzte bekannte Shell ohne Fetch — fuer die Fail-Closed-Kaskade
     * (unerwartete Exception -> ungepatchte Shell serven). Fallback-Reihenfolge:
     * angefragte Sprache -> Root -> irgendeine.
     */
    peek(lang: SamplePollLanguage | null): string | null;
}

interface ShellCacheEntry {
    body: string;
    etag: string | null;
    freshUntil: number;
    /** nach einer fehlgeschlagenen Revalidierung: auch forceRevalidate wartet den Backoff ab */
    backoffUntil: number;
}

const DEFAULT_TTL_MS = 10 * 1000;
const DEFAULT_ERROR_BACKOFF_MS = 60 * 1000;
const ROOT_KEY = 'root';

export function createShellCache(options: ShellCacheOptions): ShellCache {
    const ttlMs = options.ttlMs ?? DEFAULT_TTL_MS;
    const errorBackoffMs = options.errorBackoffMs ?? DEFAULT_ERROR_BACKOFF_MS;
    const now = options.now ?? (() => Date.now());
    const baseUrl = options.baseUrl.replace(/\/+$/, '');
    const entries: Record<string, ShellCacheEntry> = {};
    const inFlight: Record<string, Promise<string>> = {};

    function shellUrl(key: string): string {
        return key === ROOT_KEY ? baseUrl + '/_spa.html' : baseUrl + '/' + key + '/_spa.html';
    }

    async function fetchShell(key: string, etag: string | null): Promise<'notModified' | ShellCacheEntry> {
        const headers: Record<string, string> = {};
        if (etag) {
            headers['If-None-Match'] = etag;
        }
        const response = await options.fetchImpl(shellUrl(key), headers);
        if (response.status === 304) {
            return 'notModified';
        }
        if (!response.ok) {
            throw new Error('shell fetch ' + shellUrl(key) + ' -> ' + response.status);
        }
        const body = await response.text();
        return { body, etag: response.headers.get('etag'), freshUntil: now() + ttlMs, backoffUntil: 0 };
    }

    async function refresh(key: string): Promise<string> {
        const existing = entries[key];
        if (existing) {
            // Revalidierung: 304 -> TTL-Reset; Fehler -> stale-if-error
            try {
                const result = await fetchShell(key, existing.etag);
                if (result === 'notModified') {
                    existing.freshUntil = now() + ttlMs;
                } else {
                    entries[key] = result;
                }
            } catch (error) {
                // stale weiterverwenden, nicht pro Request neu versuchen
                existing.freshUntil = now() + errorBackoffMs;
                existing.backoffUntil = existing.freshUntil;
            }
            return entries[key].body;
        }
        // Cold-Miss: einmal retryen, dann werfen (Aufrufer -> Fail-Closed-Kaskade)
        let result: 'notModified' | ShellCacheEntry;
        try {
            result = await fetchShell(key, null);
        } catch (firstError) {
            result = await fetchShell(key, null);
        }
        if (result === 'notModified') {
            // 304 ohne If-None-Match sollte nicht vorkommen
            throw new Error('unexpected 304 for ' + shellUrl(key));
        }
        entries[key] = result;
        return entries[key].body;
    }

    return {
        getShell(lang: SamplePollLanguage | null, getOptions?: GetShellOptions): Promise<string> {
            const key = lang || ROOT_KEY;
            const entry = entries[key];
            const validUntil = entry ? (getOptions?.forceRevalidate ? entry.backoffUntil : entry.freshUntil) : 0;
            if (entry && validUntil > now()) {
                return Promise.resolve(entry.body);
            }
            if (!inFlight[key]) {
                inFlight[key] = refresh(key).finally(() => {
                    delete inFlight[key];
                });
            }
            return inFlight[key];
        },
        peek(lang: SamplePollLanguage | null): string | null {
            const key = lang || ROOT_KEY;
            if (entries[key]) {
                return entries[key].body;
            }
            if (entries[ROOT_KEY]) {
                return entries[ROOT_KEY].body;
            }
            const keys = Object.keys(entries);
            return keys.length > 0 ? entries[keys[0]].body : null;
        },
    };
}
